import { useLayoutEffect, useState } from "react";

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 8;

type Align = "left" | "right" | "stretch";

type Options = {
  /** The trigger element the popover anchors to. */
  anchorEl: HTMLElement | null;
  /** The popover root, used to measure its size for clamping/flipping. */
  popoverEl: HTMLElement | null;
  /**
   * Horizontal alignment relative to the anchor.
   * - "left"    → popover's left edge matches the anchor's left
   * - "right"   → popover's right edge matches the anchor's right
   * - "stretch" → popover width matches the anchor width
   */
  align?: Align;
};

export type PopoverPosition = {
  top: number;
  left: number;
  /** Set when align is "stretch" — popover width matches anchor width. */
  width?: number;
};

/**
 * Computes a viewport-clamped position for a portal-rendered popover. Prefers
 * placing the popover below the anchor; flips above when below would overflow
 * the viewport. Clamps horizontally so the popover never lands off-screen.
 *
 * Listens to scroll (capture phase, so it catches scroll inside modals) and
 * resize so the popover follows the trigger.
 */
export function usePopoverPosition({
  anchorEl,
  popoverEl,
  align = "left",
}: Options): PopoverPosition | null {
  const [pos, setPos] = useState<PopoverPosition | null>(null);

  useLayoutEffect(() => {
    if (!anchorEl) {
      setPos(null);
      return;
    }

    function compute(): PopoverPosition {
      const a = anchorEl!.getBoundingClientRect();
      const popW = popoverEl?.offsetWidth ?? a.width;
      const popH = popoverEl?.offsetHeight ?? 0;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let left: number;
      let width: number | undefined;

      if (align === "stretch") {
        left = a.left;
        width = a.width;
      } else if (align === "right") {
        left = a.right - popW;
      } else {
        left = a.left;
      }

      const effectiveW = width ?? popW;
      left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - effectiveW - VIEWPORT_MARGIN));

      let top = a.bottom + ANCHOR_GAP;
      if (popH > 0 && top + popH > vh - VIEWPORT_MARGIN) {
        const aboveTop = a.top - popH - ANCHOR_GAP;
        if (aboveTop >= VIEWPORT_MARGIN) {
          top = aboveTop;
        } else {
          top = Math.max(VIEWPORT_MARGIN, vh - popH - VIEWPORT_MARGIN);
        }
      }

      return { top, left, width };
    }

    setPos(compute());

    function onReflow() {
      setPos(compute());
    }

    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);

    let ro: ResizeObserver | null = null;
    if (popoverEl && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(onReflow);
      ro.observe(popoverEl);
    }

    return () => {
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
      ro?.disconnect();
    };
  }, [anchorEl, popoverEl, align]);

  return pos;
}
